/**
 * Connect Behavior
 *
 * Handles edge creation by dragging from node handles.
 *
 * Two modes:
 * - 'always': Handles shown on hover/selected nodes, must drag from handle
 * - 'tool-only': Only active when connect tool is active, can drag from anywhere on node
 *
 * Features:
 * - Hover handles to highlight them
 * - Drag from handle (or node in tool mode) to start connection
 * - Preview line while dragging
 * - Drop on another handle (or node in tool mode) to create edge
 * - Escape to cancel
 */

import type { ConnectBehaviorContext } from './types';
import type { Point, HandlePosition, EditorNode } from '../types';
import {
	formatHandleId,
	parseHandleId,
	getHandlePosition,
	getAllHandlePositions,
} from '../types';
import * as viewState from '../view-state';

/** Default handle radius for hit testing (in world units) */
const DEFAULT_HANDLE_RADIUS = 12;

/**
 * Hit test for handles on a node.
 * Returns the handle ID if point is within handle radius, null otherwise.
 */
function hitTestHandles(
	ctx: ConnectBehaviorContext,
	nodeId: string,
	point: Point
): string | null {
	const node = ctx.getNodeById(nodeId);
	if (!node) return null;

	const radius = ctx.handleRadius ?? DEFAULT_HANDLE_RADIUS;
	const handles = getAllHandlePositions(node);

	for (const { position, point: handlePoint } of handles) {
		const dx = point.x - handlePoint.x;
		const dy = point.y - handlePoint.y;
		const distance = Math.sqrt(dx * dx + dy * dy);

		if (distance <= radius) {
			return formatHandleId(nodeId, position);
		}
	}

	return null;
}

/**
 * Find the nearest handle on a node to a given point.
 */
function findNearestHandle(node: EditorNode, point: Point): { position: HandlePosition; point: Point } {
	const handles = getAllHandlePositions(node);
	let nearest = handles[0];
	let nearestDist = Infinity;

	for (const handle of handles) {
		const dx = point.x - handle.point.x;
		const dy = point.y - handle.point.y;
		const dist = dx * dx + dy * dy;
		if (dist < nearestDist) {
			nearestDist = dist;
			nearest = handle;
		}
	}

	return nearest;
}

/**
 * Register connect behavior handlers.
 * Returns cleanup function to unregister all handlers.
 */
export function registerConnectBehavior(ctx: ConnectBehaviorContext): () => void {
	const {
		input,
		getViewState,
		updateViewState,
		hitTest,
		getNodes,
		screenToWorld,
		createEdge,
		connectionMode,
		isConnectToolActive,
		onConnectionComplete,
		log,
	} = ctx;
	const unsubscribers: (() => void)[] = [];

	// =========================================================================
	// Handle Hover Detection
	// =========================================================================

	// Track handle hover during mousemove
	unsubscribers.push(
		input.on('mousemove', { mode: '*' }, (inputCtx) => {
			const state = getViewState();
			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			const mode = connectionMode();
			const toolActive = isConnectToolActive();

			// In tool-only mode without tool active or connecting, skip handle detection
			if (mode === 'tool-only' && !toolActive && !state.connecting) {
				if (state.hovered.handleId) {
					updateViewState(viewState.setHoveredHandle(state, null));
				}
				return;
			}

			// When connecting OR when connect tool is active, check ALL nodes for handles
			if (state.connecting || toolActive) {
				const sourceNodeId = state.connecting?.sourceNode;
				let foundHandle: string | null = null;

				for (const node of getNodes()) {
					if (node.id === sourceNodeId) continue; // Can't connect to self

					const handleId = hitTestHandles(ctx, node.id, point);
					if (handleId) {
						foundHandle = handleId;
						break;
					}
				}

				if (foundHandle !== state.hovered.handleId) {
					updateViewState(viewState.setHoveredHandle(state, foundHandle));
				}
				return;
			}

			// In 'always' mode without tool - check handles on hovered or selected nodes
			const hoveredNodeId = state.hovered.nodeId;
			const selectedIds = viewState.getSelectedIds(state);

			// Nodes that should show handles: hovered node + selected nodes
			const nodesWithHandles = new Set<string>();
			if (hoveredNodeId) nodesWithHandles.add(hoveredNodeId);
			for (const id of selectedIds) {
				const nodes = getNodes();
				if (nodes.some((n) => n.id === id)) {
					nodesWithHandles.add(id);
				}
			}

			// Check handles on these nodes
			let foundHandle: string | null = null;
			for (const nodeId of nodesWithHandles) {
				const handleId = hitTestHandles(ctx, nodeId, point);
				if (handleId) {
					foundHandle = handleId;
					break;
				}
			}

			if (foundHandle !== state.hovered.handleId) {
				updateViewState(viewState.setHoveredHandle(getViewState(), foundHandle));
			}
		})
	);

	// =========================================================================
	// Start Connection - Default Mode (always mode: from handle)
	// =========================================================================

	unsubscribers.push(
		input.on('mousedown', { mode: 'default' }, (inputCtx) => {
			if (inputCtx.button === 1) return; // Ignore middle mouse button

			const mode = connectionMode();
			const state = getViewState();
			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);

			// In 'always' mode, must click on a handle
			if (mode === 'always') {
				const hoveredHandle = state.hovered.handleId;
				if (!hoveredHandle) return;

				const parsed = parseHandleId(hoveredHandle);
				if (!parsed) return;

				updateViewState(
					viewState.startConnecting(state, parsed.nodeId, hoveredHandle, point)
				);
				input.pushMode('connecting');
				log?.('connect-start', `from ${hoveredHandle}`);
				inputCtx.stopPropagation();
			}
			// In 'tool-only' mode, default mode doesn't start connections
		})
	);

	// =========================================================================
	// Start Connection - Tool Mode (when connect tool is active)
	// =========================================================================

	unsubscribers.push(
		input.on('mousedown', { mode: 'tool' }, (inputCtx) => {
			if (inputCtx.button === 1) return; // Ignore middle mouse button

			const toolActive = isConnectToolActive();

			// Only handle if connect tool is active
			if (!toolActive) return;

			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			const hit = hitTest(point);

			// Must click on a node
			if (!hit.nodeId) return;

			const node = ctx.getNodeById(hit.nodeId);
			if (!node) return;

			// Find nearest handle to the click point
			const nearest = findNearestHandle(node, point);
			const handleId = formatHandleId(hit.nodeId, nearest.position);

			const state = getViewState();
			updateViewState(
				viewState.startConnecting(state, hit.nodeId, handleId, point)
			);
			input.pushMode('connecting');
			log?.('connect-start', `from ${handleId} (tool mode)`);
			inputCtx.stopPropagation();
		})
	);

	// =========================================================================
	// Connection Mode Handlers
	// =========================================================================

	// Update preview line position
	unsubscribers.push(
		input.on('mousemove', { mode: 'connecting' }, (inputCtx) => {
			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
			updateViewState(viewState.updateConnecting(getViewState(), point));
		})
	);

	// Finish connection on mouseup
	unsubscribers.push(
		input.on('mouseup', { mode: 'connecting' }, (inputCtx) => {
			const state = getViewState();
			const mode = connectionMode();
			const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);

			if (!state.connecting) {
				input.popMode('connecting');
				return;
			}

			const sourceParsed = parseHandleId(state.connecting.sourceHandle);
			if (!sourceParsed) {
				updateViewState(viewState.finishConnecting(state));
				updateViewState(viewState.setHoveredHandle(getViewState(), null));
				input.popMode('connecting');
				return;
			}

			let targetNodeId: string | null = null;

			// Check if hovering a handle
			const hoveredHandle = state.hovered.handleId;
			if (hoveredHandle) {
				const targetParsed = parseHandleId(hoveredHandle);
				if (targetParsed && targetParsed.nodeId !== sourceParsed.nodeId) {
					targetNodeId = targetParsed.nodeId;
				}
			}

			// When connect tool was used to start (check if sourceHandle was auto-assigned),
			// also allow dropping anywhere on a node
			if (!targetNodeId) {
				const hit = hitTest(point);
				if (hit.nodeId && hit.nodeId !== sourceParsed.nodeId) {
					targetNodeId = hit.nodeId;
				}
			}

			if (targetNodeId) {
				createEdge(sourceParsed.nodeId, targetNodeId);
				log?.('connect-end', `${sourceParsed.nodeId} -> ${targetNodeId}`);
				// Notify that connection was completed (for tool deactivation)
				onConnectionComplete?.();
			} else if (state.connecting) {
				log?.('connect-cancel', 'no valid target');
			}

			updateViewState(viewState.finishConnecting(getViewState()));
			updateViewState(viewState.setHoveredHandle(getViewState(), null));
			input.popMode('connecting');
		})
	);

	// Cancel connection on Escape
	unsubscribers.push(
		input.on(
			'keydown',
			'escape',
			{
				mode: 'connecting',
				command: { id: 'connect.cancel', name: 'Cancel Connection' },
			},
			() => {
				log?.('connect-cancel', 'escape pressed');
				updateViewState(viewState.cancelConnecting(getViewState()));
				updateViewState(viewState.setHoveredHandle(getViewState(), null));
				input.popMode('connecting');
			}
		)
	);

	// =========================================================================
	// Cleanup
	// =========================================================================

	return () => {
		for (const unsub of unsubscribers) {
			unsub();
		}
	};
}
